/**
 * Firebase Analytics Service (GA4 under the hood)
 * Handles analytics tracking via Firebase SDK with localhost filtering
 */

import { analytics as firebaseAnalytics } from './firebase';
import { logEvent, setUserId as fbSetUserId, setUserProperties as fbSetUserProperties, setAnalyticsCollectionEnabled } from 'firebase/analytics';

let initialized = false;
const eventQueue = [];

const isBrowser = () => typeof window !== 'undefined';
const isLocalhost = () => {
  if (!isBrowser()) return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
};

const normalizeParams = (params = {}) => {
  const normalized = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (typeof value === 'boolean') {
      normalized[key] = value ? 1 : 0;
    } else {
      normalized[key] = value;
    }
  });
  return normalized;
};

const flushQueue = () => {
  if (!initialized || isLocalhost()) return;
  while (eventQueue.length > 0) {
    const item = eventQueue.shift();
    try {
      if (item.type === 'event') {
        logEvent(firebaseAnalytics, item.name, item.params);
      } else if (item.type === 'page_view') {
        logEvent(firebaseAnalytics, 'page_view', item.params);
      } else if (item.type === 'user_id') {
        fbSetUserId(firebaseAnalytics, item.userId || null);
      } else if (item.type === 'user_properties') {
        fbSetUserProperties(firebaseAnalytics, item.properties || {});
      }
    } catch (_) {
      // swallow
    }
  }
};

export const initializeAnalytics = async () => {
  if (!isBrowser()) return false;
  if (initialized) return true;
  try {
    const enableCollection = !isLocalhost();
    setAnalyticsCollectionEnabled(firebaseAnalytics, enableCollection);
    initialized = true;
    console.log(`Firebase Analytics initialized (collection ${enableCollection ? 'enabled' : 'disabled for localhost'})`);
    flushQueue();
    return true;
  } catch (e) {
    console.log('Firebase Analytics failed to initialize', e);
    return false;
  }
};

// Track page views
export const trackPageView = (path, title) => {
  // Never send query parameters. Console links can carry invite, coupon, and
  // checkout identifiers that do not belong in analytics.
  const pageLocation = isBrowser() ? `${window.location.origin}${path}` : undefined;
  const params = normalizeParams({
    page_title: title,
    page_path: path,
    page_location: pageLocation
  });
  if (!initialized || isLocalhost()) {
    eventQueue.push({ type: 'page_view', params });
    return;
  }
  logEvent(firebaseAnalytics, 'page_view', params);
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  const params = normalizeParams(parameters);
  if (!initialized || isLocalhost()) {
    eventQueue.push({ type: 'event', name: eventName, params });
    return;
  }
  logEvent(firebaseAnalytics, eventName, params);
};

// User Authentication Events
export const trackAuthEvent = (action, method = 'google') => {
  trackEvent('auth_action', {
    event_category: 'Authentication',
    event_label: method,
    action: action, // 'sign_in', 'sign_up', 'sign_out'
  });
};

// Presentation Events
export const trackPresentationEvent = (action, presentationId = null, additionalData = {}) => {
  trackEvent('presentation_action', {
    event_category: 'Presentation',
    action: action, // 'create', 'open', 'save', 'share', 'export', 'delete'
    presentation_id: presentationId,
    ...additionalData
  });
};

// Slide Events
export const trackSlideEvent = (action, slideData = {}) => {
  trackEvent('slide_action', {
    event_category: 'Slide',
    action: action, // 'add', 'delete', 'reorder', 'edit', 'duplicate'
    slide_position: slideData.position,
    slide_type: slideData.type,
    ...slideData
  });
};

// AI Chat Events
export const trackAIChatEvent = (action, chatData = {}) => {
  trackEvent('ai_chat_action', {
    event_category: 'AI Chat',
    action: action, // 'start_chat', 'send_message', 'regenerate', 'apply_suggestion'
    message_length: chatData.messageLength,
    chat_type: chatData.chatType,
    model_used: chatData.model,
    ...chatData
  });
};

// Settings Events
export const trackSettingsEvent = (action, setting, value) => {
  trackEvent('settings_change', {
    event_category: 'Settings',
    action: action, // 'change_theme', 'update_preference', 'export_settings'
    setting_name: setting,
    setting_value: value
  });
};

// Error Tracking
export const trackError = (error, context = {}) => {
  trackEvent('exception', {
    description: error.message || 'Unknown error',
    fatal: false,
    error_context: context.context || 'general',
    error_component: context.component,
    ...context
  });
};

// Performance Tracking
export const trackPerformance = (metricName, value, context = {}) => {
  trackEvent('performance_metric', {
    event_category: 'Performance',
    metric_name: metricName,
    metric_value: value,
    ...context
  });
};

// E-commerce / Subscription Events
export const trackSubscriptionEvent = (action, subscriptionData = {}) => {
  trackEvent('subscription_action', {
    event_category: 'Subscription',
    action: action, // 'upgrade', 'downgrade', 'cancel', 'renew'
    plan_type: subscriptionData.planType,
    plan_price: subscriptionData.price,
    currency: subscriptionData.currency || 'USD',
    ...subscriptionData
  });
};

// User Properties
export const setUserProperties = (properties) => {
  const normalized = normalizeParams(properties || {});
  if (!initialized || isLocalhost()) {
    eventQueue.push({ type: 'user_properties', properties: normalized });
    return;
  }
  fbSetUserProperties(firebaseAnalytics, normalized);
};

// Set user ID for cross-device tracking
export const setUserId = (userId) => {
  if (!initialized || isLocalhost()) {
    eventQueue.push({ type: 'user_id', userId });
    return;
  }
  fbSetUserId(firebaseAnalytics, userId || null);
};

// Consent and Privacy
export const updateConsent = (consentSettings) => {
  // No-op for Firebase Analytics web SDK; handle consent at app level if needed
};

// Export all tracking functions for easy access
export const analytics = {
  initialize: initializeAnalytics,
  trackPageView,
  trackEvent,
  trackAuth: trackAuthEvent,
  trackPresentation: trackPresentationEvent,
  trackSlide: trackSlideEvent,
  trackAIChat: trackAIChatEvent,
  trackSettings: trackSettingsEvent,
  trackError,
  trackPerformance,
  trackSubscription: trackSubscriptionEvent,
  setUserProperties,
  setUserId,
  updateConsent
};

export default analytics;
